import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';
import { API_CONFIG } from '../../../utils/apiConfig';
import { FirebaseService } from '../../../services/firebaseService';
import './customer.css';

function CustomerList({ onEdit = () => {}, refreshTrigger }) {
  const { currentUser } = useAuth();
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    const fetchCustomers = async () => {
      let customersData = [];
      
      try {
        console.log('🔍 CustomerList: Fetching customers...');
        console.log('🔍 CustomerList: API_CONFIG.BASE_URL:', API_CONFIG.BASE_URL);
        console.log('🔍 CustomerList: USE_FIREBASE_ONLY:', API_CONFIG.USE_FIREBASE_ONLY);
        
        // Try API first (only if not in Firebase-only mode)
        if (!API_CONFIG.USE_FIREBASE_ONLY && API_CONFIG.BASE_URL && currentUser) {
          try {
            const idToken = await currentUser.getIdToken(true);
            console.log('🔍 CustomerList: Trying API fetch...');
            
            const res = await axios.get(`${API_CONFIG.BASE_URL}/api/customers`, {
              headers: { Authorization: `Bearer ${idToken}` },
            });
            
            console.log('✅ CustomerList: API Response successful:', res.data.length, 'customers');
            customersData = Array.isArray(res.data) ? res.data : [];
          } catch (apiError) {
            console.log('⚠️ CustomerList: API fetch failed:', apiError.message);
            // Continue to Firebase fallback
          }
        } else {
          console.log('🔄 CustomerList: Skipping API call - using Firebase direct access');
        }
        
        // Try Firebase if no results from API or API not available
        if (customersData.length === 0) {
          try {
            console.log('🔄 CustomerList: Trying Firebase fetch...');
            const firebaseCustomers = await FirebaseService.getCustomers();
            
            if (firebaseCustomers && firebaseCustomers.length > 0) {
              console.log('✅ CustomerList: Firebase fetch successful:', firebaseCustomers.length, 'customers');
              customersData = firebaseCustomers;
            }
          } catch (firebaseError) {
            console.log('⚠️ CustomerList: Firebase fetch failed:', firebaseError.message);
          }
        }
        
        // Final fallback to localStorage
        if (customersData.length === 0) {
          try {
            console.log('🔄 CustomerList: Trying localStorage fallback...');
            const storedCustomers = JSON.parse(localStorage.getItem('customers') || '[]');
            
            if (storedCustomers && storedCustomers.length > 0) {
              console.log('✅ CustomerList: localStorage fetch successful:', storedCustomers.length, 'customers');
              customersData = storedCustomers;
            }
          } catch (storageError) {
            console.log('⚠️ CustomerList: localStorage fetch failed:', storageError.message);
          }
        }
        
        console.log('🎯 CustomerList: Final customer count:', customersData.length);
        setCustomers(customersData);
        
      } catch (err) {
        console.error('❌ CustomerList: Error in fetchCustomers:', err);
        setCustomers([]);
      }
    };

    fetchCustomers();
  }, [currentUser, refreshTrigger]);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        let deleteSuccess = false;
        
        // Find the customer to get their customerId for Firebase deletion
        const customerToDelete = customers.find(c => (c.id || c._id) === id);
        const customerId = customerToDelete?.customerId;
        
        // Try API deletion first (only if not in Firebase-only mode)
        if (!API_CONFIG.USE_FIREBASE_ONLY && API_CONFIG.BASE_URL && currentUser) {
          try {
            const idToken = await currentUser.getIdToken(true);
            const deleteUrl = `${API_CONFIG.BASE_URL}/api/customers/${id}`;
            console.log('🔍 CustomerList: Attempting API deletion with URL:', deleteUrl);
            console.log('🔍 CustomerList: API_CONFIG.BASE_URL:', API_CONFIG.BASE_URL);
            console.log('🔍 CustomerList: Customer ID to delete:', id);
            
            await axios.delete(deleteUrl, {
              headers: { Authorization: `Bearer ${idToken}` },
            });
            console.log('✅ CustomerList: Customer deleted via API');
            deleteSuccess = true;
          } catch (apiError) {
            console.log('⚠️ CustomerList: API deletion failed:', apiError.message);
            console.log('⚠️ CustomerList: API Error details:', apiError.response?.data);
            console.log('⚠️ CustomerList: Full error:', apiError);
          }
        } else {
          console.log('🔄 CustomerList: Skipping API deletion - using Firebase direct access');
        }
        
        // Try Firebase deletion if API failed or not available
        if (!deleteSuccess && customerId) {
          try {
            console.log('🔄 CustomerList: Attempting Firebase deletion...');
            
            // Try Firebase deletion first
            const firebaseDeleteSuccess = await FirebaseService.deleteCustomer(customerId);
            if (firebaseDeleteSuccess) {
              console.log('✅ CustomerList: Customer deleted from Firebase');
              deleteSuccess = true;
            } else {
              // Fallback to localStorage deletion
              console.log('🔄 CustomerList: Firebase deletion failed, trying localStorage...');
              const storedCustomers = JSON.parse(localStorage.getItem('customers') || '[]');
              const updatedCustomers = storedCustomers.filter(c => c.customerId !== customerId);
              localStorage.setItem('customers', JSON.stringify(updatedCustomers));
              
              console.log('✅ CustomerList: Customer deleted from localStorage');
              deleteSuccess = true;
            }
          } catch (error) {
            console.log('⚠️ CustomerList: Firebase/localStorage deletion failed:', error.message);
          }
        }
        
        if (deleteSuccess) {
          // Update the UI immediately
          setCustomers(customers.filter((customer) => (customer.id || customer._id) !== id));
          alert('Customer deleted successfully!');
          
          // Force refresh the customer list to ensure data consistency
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        } else {
          console.error('❌ CustomerList: All deletion methods failed');
          alert('Failed to delete customer. Please try again or contact support.');
        }
        
      } catch (err) {
        console.error('❌ CustomerList: Delete error:', err);
        alert('Failed to delete customer: ' + err.message);
      }
    }
  };

  const handleEdit = (customer, e) => {
    e.stopPropagation(); // Prevent click from bubbling to flip book
    if (onEdit && typeof onEdit === 'function') {
      onEdit(customer.id || customer._id);
    }
  };

  // Sort customers by customerId in ascending order
  // Ensure customers is always an array before sorting
  const sortedCustomers = Array.isArray(customers) ? [...customers].sort((a, b) => {
    const idA = a.customerId || '';
    const idB = b.customerId || '';
    
    // Extract numeric part from customer ID (e.g., "CUST-1" -> 1)
    const numA = parseInt(idA.replace(/\D/g, '')) || 0;
    const numB = parseInt(idB.replace(/\D/g, '')) || 0;
    
    return numA - numB;
  }) : [];

  console.log('🔍 CustomerList: Rendering with customers:', customers.length);
  console.log('🔍 CustomerList: Sorted customers:', sortedCustomers.length);
  
  return (
    <div className="customer-list-container">
      <div className="table-responsive">
        <table className="customer-table">
          <thead>
            <tr>
              <th>Customer ID</th>
              <th>Name</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Address</th>
              <th>GSTIN</th>
              <th>Company</th>
              <th>Created By</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedCustomers.length === 0 ? (
              <tr key="no-customers">
                <td colSpan="9" style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                  No customers found. Add your first customer!
                </td>
              </tr>
            ) : (
              sortedCustomers.map((customer) => (
                <tr key={customer.id || customer._id}>
                  <td>{customer.customerId || customer._id}</td>
                  <td>{customer.customerName}</td>
                  <td>{customer.customerContact.phone}</td>
                  <td>{customer.customerContact.email}</td>
                  <td>{customer.customerContact.address}</td>
                  <td>{customer.customerContact.gstin}</td>
                  <td>{customer.company}</td>
                  <td>{customer.createdBy}</td>
                  <td>
                    <button
                      className="edit-btn"
                      onClick={(e) => handleEdit(customer, e)}
                    >
                      <i className="fa-solid fa-pen-to-square"></i>
                    </button>
                    <button
                      className="delete-btn danger"
                      onClick={(e) => handleDelete(customer.id || customer._id, e)}
                    >
                    <i className="fa-solid fa-trash"></i>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default CustomerList;